import {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogCancel,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import { formatBytes } from "@veil/ui/lib/format";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import type { RawBundle } from "./types";

export interface DeleteUploadDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	bundle: RawBundle | null;
	confirming: boolean;
	onConfirm: () => void;
}

export function DeleteUploadDialog({
	open,
	onOpenChange,
	bundle,
	confirming,
	onConfirm,
}: DeleteUploadDialogProps) {
	const consumed = Boolean(bundle?.consumedById);

	return (
		<ResponsiveDialog variant="alert" open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent>
				<ResponsiveDialogHeader>
					<ResponsiveDialogTitle>Delete upload</ResponsiveDialogTitle>
					<ResponsiveDialogDescription>
						The raw bundle is removed from the release server and its file
						deleted from disk. This action cannot be undone.
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>

				{bundle ? (
					<dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 py-2 text-sm">
						<dt className="text-muted-foreground">Filename</dt>
						<dd className="truncate font-mono text-xs" title={bundle.filename}>
							{bundle.filename}
						</dd>
						<dt className="text-muted-foreground">Size reclaimed</dt>
						<dd className="font-mono tabular-nums">
							{formatBytes(bundle.sizeBytes)}
						</dd>
						<dt className="text-muted-foreground">Consumed by release</dt>
						<dd>{consumed ? "Yes" : "No"}</dd>
					</dl>
				) : null}

				{consumed ? (
					<div className="rounded-md border border-[var(--status-warning)]/40 bg-[var(--status-warning)]/5 p-3 text-xs">
						This bundle has already been consumed by a release. Delete is
						disabled to preserve the audit trail.
					</div>
				) : null}

				<ResponsiveDialogFooter>
					<ResponsiveDialogCancel variant="ghost" disabled={confirming}>
						Cancel
					</ResponsiveDialogCancel>
					<ResponsiveDialogAction
						variant="destructive"
						disabled={confirming || consumed || !bundle}
						onClick={(e) => {
							e.preventDefault();
							onConfirm();
						}}
					>
						{confirming ? (
							<>
								<Loader2 data-icon="inline-start" className="animate-spin" />
								Deleting
							</>
						) : (
							"Delete upload"
						)}
					</ResponsiveDialogAction>
				</ResponsiveDialogFooter>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
